import { useCallback, useEffect, useState } from 'react';
import {
  loadModelHistory,
  mergeModelHistory,
  recordModelObservation,
  saveModelHistory,
  settleModelRound,
  type ModelHistory,
} from '../lib/btc15ModelHistory';
import { getBtc15ModelHistory } from '../lib/apiClient';
import type { ModelPrediction } from '../lib/modelPredictions';
import type { ModelVersion } from '../lib/modelVersion';
import type { Btc15PredictorResponse } from '../lib/btc15Types';

const STORAGE_KEY: Record<ModelVersion, string> = {
  v2: 'whaletail.btc15V2History.v1',
  v3: 'whaletail.btc15V3History.v1',
  v4: 'whaletail.btc15V4History.v1',
  coinflip: 'whaletail.btc15CoinFlipHistory.v1',
  dice: 'whaletail.btc15DiceHistory.v1',
};

/**
 * Client-side mirror of useBtc15History, for V2/V3/V4 rather than V1: records
 * one local observation per round when a live prediction is available (the
 * daily scheduled refit may not have fit a model yet — see
 * useBtc15FittedModels), and syncs in the server's own independently-kept
 * track record (btc15-tracker-scheduled.ts keeps recording even when no
 * browser tab is open).
 */
export function useBtc15ModelHistory(version: ModelVersion) {
  const storageKey = STORAGE_KEY[version];
  const [history, setHistory] = useState<ModelHistory>(() =>
    loadModelHistory(storageKey),
  );

  const applyResponse = useCallback(
    (response: Btc15PredictorResponse, prediction: ModelPrediction | null) => {
      setHistory((current) => {
        const withObservation = recordModelObservation(current, response, prediction);
        const settled = settleModelRound(withObservation, response);
        saveModelHistory(storageKey, settled);
        return settled;
      });
    },
    [storageKey],
  );

  useEffect(() => {
    let cancelled = false;
    const syncFromServer = async () => {
      try {
        const serverHistory = await getBtc15ModelHistory(version);
        if (cancelled) return;
        setHistory((current) => {
          const { merged } = mergeModelHistory(current, serverHistory);
          saveModelHistory(storageKey, merged);
          return merged;
        });
      } catch {
        // Best-effort background sync; local recording keeps working on its own.
      }
    };
    syncFromServer();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncFromServer();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [version, storageKey]);

  return { history, applyResponse };
}
